import { Select } from 'antd';
import moment from 'moment';
import QueryString from 'qs';
import { FC, useEffect, useState } from 'react';
import { CgCloseR } from 'react-icons/cg';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { getSubjects } from '../../../api/notification.api';
import useAxios from '../../../hooks/useAxios';
import useNotification from '../../../hooks/useNotification';
import { IGetSubjects, INotification } from '../../../models/notification.model';
import { RootState } from '../../../store/rootReducer';
import Button from '../../Button';
import ErrorResponse from '../../ErrorResponse';
import FileUpload from '../../FileUpload';
import Loader from '../../Loader';
import Radio from '../../Radio';

interface IProps {
  event: any;
  submit: (data: FormData) => void;
  toSubmit: boolean;
  setToSubmit: (toSubmit: boolean) => void;
  data?: INotification|undefined;
  isTutor?: boolean;
  className?: string|undefined;
}

const NoticeEditor: FC<IProps> = ({
  event,
  submit,
  toSubmit,
  setToSubmit,
  data,
  isTutor = false,
  className,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const classCode = String(query.classCode);
  const { auth } = useSelector((state: RootState) => state.auth);
  const { chosenClass } = useSelector((state: RootState) => state.class);
  const { infoNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetSubjects>();
  
  const [classTimeSeq, setClassTimeSeq] = useState<number>();
  const [subjectCode, setSubjectCode] = useState<number>();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [alarmYN, setAlarmYN] = useState('Y');
  const [files, setFiles] = useState<any>([null]);
  const [isCreate, setIsCreate] = useState(true);
  const [sms, setSms] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<any>();
  
  useEffect(() => {
    if (event) {
      execute(getSubjects({
        semesterCourseCode: event.resource.semesterCourseCode,
        timetableSeq: event.resource.timetableSeq,
      }));
    }
  }, [event]);

  useEffect(() => {
    if (data) {
      setClassTimeSeq(data.classTimeSeq);
      setSubjectCode(data.subjectCode);
      setTitle(data.title);
      setContent(data.content);
      setAlarmYN(data.alarmYN);
      setAttachedFiles(data.files);
      setIsCreate(false);
    }
  }, [data]);

  useEffect(() => {
    if (toSubmit) {
      handleSubmit();
    }
  }, [toSubmit]);

  const onSetFile = (index: number, file: any) => {
    const _files = [...files];
    _files[index] = file;
    setFiles(_files);
  }

  const onAddFile = () => {
    const _files = [...files];
    _files.push(null);
    setFiles(_files);
  }

  const onDeleteFile = (index: number) => {
    const _files = [...files];
    _files.splice(index, 1);
    setFiles(_files);
  }

  const onDeleteAttachedFile = (index: number) => {
    const _attachedFiles = [...attachedFiles];
    _attachedFiles[index].originalName = null;
    setAttachedFiles(_attachedFiles);
  }

  const handleSubmit = () => {
    setToSubmit(false);
    if ((!isTutor && !chosenClass) || !event) return;

    if (!subjectCode) {
      return infoNotification(t('class.class.message29'));
    }
    if (!title) {
      return infoNotification(t('class.class.message30'));
    }
    if (!content) {
      return infoNotification(t('class.class.message31'));
    }

    const formData = new FormData();
    // @ts-ignore
    formData.append('classCode', isTutor ? classCode : chosenClass.classCode);
    formData.append('timetableCode', event.id);
    formData.append('classTimeSeq', String(classTimeSeq));
    formData.append('subjectCode', String(subjectCode));
    formData.append('title', title);
    formData.append('content', content);
    formData.append('alarmYN', alarmYN);
    if (isCreate) {
      formData.append('sms', String(sms));
    }

    let isAllDeleted = true;

    if (isCreate) {
      /* 생성 시 파일 처리 */
      if (files.length > 0) {
        files.forEach((item: any) => {
          if (item !== null) {
            formData.append('files', item);
          }
        });
      }
    } else {
      /* 수정 시 파일 처리 */
      if (data?.attachFileCode) {
        formData.append('attachFileCode', String(data.attachFileCode));
      }

      if (attachedFiles) {
        // 기존 첨부파일 있음
        let hasNewFiles = false;
        files.forEach((item: any) => {
          if (item) hasNewFiles = true;
        });

        let hasDeleted = false;
        attachedFiles.forEach((item: any) => {
          if (item.originalName) {
            isAllDeleted = false;
          } else {
            hasDeleted = true;
            formData.append('deleteFile', item.fileSeq);
          }
        });

        if (hasNewFiles) formData.append('fileStatus', 'update');
        else if (isAllDeleted) formData.append('fileStatus', 'delete');
        else if (hasDeleted) formData.append('fileStatus', 'update');
        else formData.append('fileStatus', 'keep');
      } else {
        // 기존 첨부파일 없음
        if (files.length > 0 && files[0] !== null) {
          // 추가 첨부파일 있음
          formData.append('fileStatus', 'create');
        } else {
          // 추가 첨부파일 없음
          formData.append('fileStatus', 'keep');
        }
      }

      if (files.length > 0) {
        files.forEach((item: any) => {
          if (item !== null) {
            formData.append('newFiles', item);
          }
        });
      }
    }

    submit(formData);
  }

  return (
    <>
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />

      <table className="table-custom">
        <colgroup>
          <col width="*" />
          <col width="*" />
          <col width="150px" />
          <col width="*" />
        </colgroup>
        <tbody>
          <tr>
            <th className="text-center">{t('class.class.column89')}</th>
            <th className="text-center">{t('class.class.column57')}</th>
            <th className="text-center">{t('class.class.column90')}</th>
            <th className="text-center">{t('class.class.column91')}</th>
          </tr>
          <tr>
            <td className="text-center">
              {
                isCreate
                  ? moment(event?.start).format('YYYY.MM.DD')
                  : moment(data?.timetableDate).format('YYYY.MM.DD')
              }
            </td>
            <td className="text-center">
              {
                isCreate
                  ? isTutor ? className : chosenClass?.className
                  : data?.className
              }
            </td>
            <td className="flex flex-justify-center">
              <div className="w-150p">
                <Select
                  className="w-100"
                  placeholder={t('common.button.select')}
                  value={subjectCode}
                  onChange={(value) => {
                    const _classTimeSeq = res?.data?.find(item => item.subjectCode === value)?.classTimeSeq;
                    setClassTimeSeq(_classTimeSeq);
                    setSubjectCode(value);
                  }}
                  disabled={!isCreate}
                >
                  {res?.data.map((item) => (
                    <Select.Option
                      key={item.subjectCode}
                      value={item.subjectCode}
                    >
                      {item.subjectName}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </td>
            <td>
              <input
                type="text"
                className="form-control"
                value={title}
                onChange={e => setTitle(e.target.value)}
              />
            </td>
          </tr>
          <tr>
            <th className="text-center">{t('class.class.column92')}</th>
            <td colSpan={3}>
              <textarea
                rows={3}
                value={content}
                onChange={e => setContent(e.target.value)}
              />
            </td>
          </tr>
          <tr>
            <th className="text-center">{t('class.class.column93')}</th>
            <td>
              {
                isCreate
                  ? auth?.korName
                  : data?.writerMemberName
              }
            </td>
            <th className="text-center">{t('class.class.column94')}</th>
            <td className="pl-4">
              <Radio
                checked={alarmYN === 'Y'}
                onChange={() => setAlarmYN('Y')}
                text={t('common.option.yes')}
                disabled={!isCreate}
              />
              <Radio
                className="ml-4"
                checked={alarmYN === 'N'}
                onChange={() => setAlarmYN('N')}
                text={t('common.option.no')}
                disabled={!isCreate}
              />
            </td>
          </tr>
          {isCreate && (
            <tr>
              <th className="text-center">{t('class.class.column95')}</th>
              <td className="pl-4" colSpan={3}>
                <Radio
                  checked={!sms}
                  onChange={() => setSms(false)}
                  text={t('class.class.option4')}
                  disabled={alarmYN === 'N'}
                />
                <Radio
                  className="ml-4"
                  checked={sms}
                  onChange={() => setSms(true)}
                  text={t('class.class.option5')}
                  disabled={alarmYN === 'N'}
                />
              </td>
            </tr>
          )}
          {
            attachedFiles?.length
              ? (
                <tr>
                  <th className="text-center">{t('archive.learn.column5')}</th>
                  <td colSpan={3}>
                    {
                      attachedFiles.map((item: any, index: number) => {
                        return item?.originalName
                          ? (
                            <div>
                              <a
                                className="download-link"
                                href={`${item.fileLocation}`}
                                download={item.originalName}
                              >
                                {item.originalName}
                              </a>
                              <span
                                className="ml-1 pointer"
                                onClick={() => onDeleteAttachedFile(index)}
                              >
                                <CgCloseR />
                              </span>
                            </div>
                          )
                          : null;
                      })
                    }
                  </td>
                </tr>
                )
              : null
          }
          <tr>
            <th className="text-center">{t('archive.exam.column7')}</th>
            <td colSpan={3}>
              {
                files.length > 0
                  ? files.map((file: any, index: number) => (
                    <div className="flex mb-2" key={index}>
                      <FileUpload
                        file={files[index]}
                        setFile={(file: any) => onSetFile(index, file)}
                      />
                      <div className="pr-t1">
                        <Button
                          className="btn btn-info ml-1"
                          onClick={() => onDeleteFile(index)}
                          text={t('common.button.delete')}
                        />
                      </div>
                    </div>
                  ))
                  : null
              }
              <Button
                className="btn btn-success btn-w100p"
                onClick={onAddFile}
                text={t('common.button.add')}
              />              
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

export default NoticeEditor;
